import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useBlockScroll } from '../../hooks/useBlockScroll';

import { CSSTransition } from 'react-transition-group';
import Button, {
  ButtonSizes,
  ButtonStyles,
} from '../../component/Button/Button';
import BetaIcon, { IconColors, IconSizes } from '../../component/Icons/Icon';
import './drawer.scss';
import Typography, { TypographyWeights } from '../Typography/Typography';

const Drawer = ({
  isOpen,
  onClose,
  title,
  body,
  footer,
  classNames,
  goBack,
}) => {
  const ref = useRef(null);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  useBlockScroll(isOpen);

  // Handle iOS keyboard resize
  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    const handleVisualViewportChange = () => {
      if (window.visualViewport) {
        setViewportHeight(window.visualViewport.height);
      }
    };

    if (!isOpen) {
      // Reset to full height when drawer is closed
      setViewportHeight(window.innerHeight);
      return;
    }

    // Immediate reset of the viewport height
    handleResize();

    // Delayed reset to handle any pending keyboard animations
    const timeoutId = setTimeout(handleResize, 100);

    window.addEventListener('resize', handleResize);

    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        'resize',
        handleVisualViewportChange
      );
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          'resize',
          handleVisualViewportChange
        );
      }
    };
  }, [isOpen]);

  const handleOverlayClick = (event) => {
    if (ref.current && !ref.current.contains(event.target)) {
      onClose();
    }
  };

  const handleSwipeToClose = (event) => {
    if (event.touches[0].clientY > 50) {
      onClose();
    }
  };

  const renderTitle = () => {
    if (typeof title === 'string') {
      return (
        <Typography variant="p2" weight={TypographyWeights['semi-bold']}>
          {title}
        </Typography>
      );
    }
    return title;
  };

  return createPortal(
    <CSSTransition
      classNames="drawer--overlay"
      in={isOpen}
      mountOnEnter={true}
      unmountOnExit={true}
      appear={true}
      timeout={200}
    >
      <div
        className="drawer--overlay"
        onClick={handleOverlayClick}
        style={{
          height: `${viewportHeight}px`,
          maxHeight: `${viewportHeight}px`,
        }}
      >
        <CSSTransition
          classNames={classNames ? classNames : 'drawer'}
          in={isOpen}
          mountOnEnter={true}
          unmountOnExit={true}
          appear={true}
          timeout={200}
        >
          <div
            ref={ref}
            className={`drawer ${classNames}`}
            style={{
              height: `${viewportHeight - 16}px`,
              maxHeight: `${viewportHeight - 16}px`,
            }}
          >
            <div className="drawer__header" onTouchMove={handleSwipeToClose}>
              <div className="drawer__header--handler" />
              <div className="drawer__header--title">{renderTitle()}</div>
              <Button
                classNames={'drawer__close-button'}
                onClick={onClose}
                style={ButtonStyles.text}
                size={ButtonSizes.xs}
                iconRight={
                  <BetaIcon
                    name={'close'}
                    color={IconColors.regular}
                    size={IconSizes[16]}
                  />
                }
              />
              {goBack && (
                <Button
                  classNames={'drawer__back-button'}
                  onClick={goBack}
                  style={ButtonStyles.text}
                  size={ButtonSizes.xs}
                  iconRight={
                    <BetaIcon
                      name={'arrowLeft'}
                      color={IconColors.regular}
                      size={IconSizes[16]}
                    />
                  }
                />
              )}
            </div>
            <div className={`drawer__body ${classNames}__body`}>{body}</div>
            {footer && <div className="drawer__footer">{footer}</div>}
          </div>
        </CSSTransition>
      </div>
    </CSSTransition>,
    document.body
  );
};

Drawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  goBack: PropTypes.func,
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  body: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
    .isRequired,
  footer: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};
export default Drawer;
